<template>
  <component
    :is="as"
    :class="textClasses"
  >
    <slot />
  </component>
</template>

<script setup>
import { computed } from 'vue'
import { TEXT_COLORS } from '@/constants/uiColors'

const props = defineProps({
  // HTML element to render
  as: {
    type: String,
    default: 'p',
    validator: (value) => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'div', 'label'].includes(value)
  },
  
  // Variant (defines the visual style)
  variant: {
    type: String,
    default: 'body',
    validator: (value) => [
      // Headings
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      // Body text
      'body', 'body-lg', 'body-sm',
      // Specialized
      'caption', 'label', 'overline',
      // Display (hero text)
      'display', 'display-sm'
    ].includes(value)
  },
  
  // Weight
  weight: {
    type: String,
    default: null,
    validator: (value) => [null, 'normal', 'medium', 'semibold', 'bold', 'extrabold'].includes(value)
  },
  
  // Color
  color: {
    type: String,
    default: 'default',
    validator: (value) => [
      'default', 'primary', 'secondary', 'muted', 'accent',
      'success', 'warning', 'danger', 'info'
    ].includes(value)
  },
  
  // Alignment
  align: {
    type: String,
    default: null,
    validator: (value) => [null, 'left', 'center', 'right', 'justify'].includes(value)
  },
  
  // Truncate
  truncate: {
    type: Boolean,
    default: false
  },
  
  // Line clamp (multiline truncate)
  lineClamp: {
    type: [Number, null],
    default: null
  },
  
  // Responsive (enables responsive sizing)
  responsive: {
    type: Boolean,
    default: false
  },
  
  // Italic
  italic: {
    type: Boolean,
    default: false
  },
  
  // Underline
  underline: {
    type: Boolean,
    default: false
  },
  
  // Transform
  transform: {
    type: String,
    default: null,
    validator: (value) => [null, 'uppercase', 'lowercase', 'capitalize', 'normal'].includes(value)
  },
  
  // No wrap
  nowrap: {
    type: Boolean,
    default: false
  },
  
  // Custom classes
  class: {
    type: String,
    default: ''
  }
})

// Variant configurations
const variantConfig = {
  // Headings
  h1: {
    size: 'text-4xl md:text-5xl lg:text-6xl',
    weight: 'font-bold',
    lineHeight: 'leading-tight',
    letterSpacing: 'tracking-tight'
  },
  h2: {
    size: 'text-3xl md:text-4xl lg:text-5xl',
    weight: 'font-bold',
    lineHeight: 'leading-tight',
    letterSpacing: 'tracking-tight'
  },
  h3: {
    size: 'text-2xl md:text-3xl',
    weight: 'font-semibold',
    lineHeight: 'leading-snug',
    letterSpacing: 'tracking-tight'
  },
  h4: {
    size: 'text-xl md:text-2xl',
    weight: 'font-semibold',
    lineHeight: 'leading-snug',
    letterSpacing: 'tracking-tight'
  },
  h5: {
    size: 'text-lg md:text-xl',
    weight: 'font-semibold',
    lineHeight: 'leading-normal',
    letterSpacing: 'tracking-normal'
  },
  h6: {
    size: 'text-base md:text-lg',
    weight: 'font-semibold',
    lineHeight: 'leading-normal',
    letterSpacing: 'tracking-normal'
  },
  
  // Display (Hero text)
  display: {
    size: 'text-5xl md:text-6xl lg:text-7xl',
    weight: 'font-extrabold',
    lineHeight: 'leading-none',
    letterSpacing: 'tracking-tighter'
  },
  'display-sm': {
    size: 'text-4xl md:text-5xl lg:text-6xl',
    weight: 'font-extrabold',
    lineHeight: 'leading-none',
    letterSpacing: 'tracking-tighter'
  },
  
  // Body text
  'body-lg': {
    size: 'text-lg',
    weight: 'font-normal',
    lineHeight: 'leading-relaxed',
    letterSpacing: 'tracking-normal'
  },
  body: {
    size: 'text-base',
    weight: 'font-normal',
    lineHeight: 'leading-normal',
    letterSpacing: 'tracking-normal'
  },
  'body-sm': {
    size: 'text-sm',
    weight: 'font-normal',
    lineHeight: 'leading-normal',
    letterSpacing: 'tracking-normal'
  },
  
  // Specialized
  caption: {
    size: 'text-xs',
    weight: 'font-normal',
    lineHeight: 'leading-tight',
    letterSpacing: 'tracking-wide'
  },
  label: {
    size: 'text-sm',
    weight: 'font-medium',
    lineHeight: 'leading-none',
    letterSpacing: 'tracking-normal'
  },
  overline: {
    size: 'text-xs',
    weight: 'font-semibold',
    lineHeight: 'leading-none',
    letterSpacing: 'tracking-wider'
  }
}

// Color classes - using TEXT_COLORS from uiColors
const colorClasses = computed(() => ({
  default: TEXT_COLORS.default.combined,
  primary: TEXT_COLORS.primary.combined,
  secondary: TEXT_COLORS.secondary.combined,
  muted: TEXT_COLORS.muted.combined,
  accent: TEXT_COLORS.accent.combined,
  success: TEXT_COLORS.success.combined,
  warning: TEXT_COLORS.warning.combined,
  danger: TEXT_COLORS.danger.combined,
  info: TEXT_COLORS.info.combined
}))

// Weight classes
const weightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold'
}

// Alignment classes
const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify'
}

// Transform classes
const transformClasses = {
  uppercase: 'uppercase',
  lowercase: 'lowercase',
  capitalize: 'capitalize',
  normal: 'normal-case'
}

// Computed classes
const textClasses = computed(() => {
  const config = variantConfig[props.variant] || variantConfig.body
  
  const classes = []
  
  // Responsive sizing or fixed sizing
  if (props.responsive || props.variant.startsWith('h') || props.variant.startsWith('display')) {
    classes.push(config.size)
  } else {
    // Non-responsive: use the first size class only
    classes.push(config.size.split(' ')[0])
  }
  
  // Weight (custom weight overrides variant weight)
  if (props.weight) {
    classes.push(weightClasses[props.weight])
  } else {
    classes.push(config.weight)
  }
  
  // Line height
  classes.push(config.lineHeight)
  
  // Letter spacing
  classes.push(config.letterSpacing)
  
  // Color
  classes.push(colorClasses.value[props.color])
  
  // Alignment
  if (props.align) {
    classes.push(alignClasses[props.align])
  }
  
  // Truncate
  if (props.truncate) {
    classes.push('truncate')
  }
  
  // Line clamp
  if (props.lineClamp) {
    classes.push(`line-clamp-${props.lineClamp}`)
  }
  
  // Italic
  if (props.italic) {
    classes.push('italic')
  }
  
  // Underline
  if (props.underline) {
    classes.push('underline')
  }
  
  // Transform
  if (props.transform) {
    classes.push(transformClasses[props.transform])
  }
  
  // No wrap
  if (props.nowrap) {
    classes.push('whitespace-nowrap')
  }
  
  // Custom classes
  if (props.class) {
    classes.push(props.class)
  }
  
  return classes.join(' ')
})
</script>

